import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import {
	useFeature,
	useIsSupport,
	useViewerRoles,
	useWorkspaceConnector,
} from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	ArchiveIcon,
	Button,
	Callout,
	CircleUserIcon,
	Container,
	FlexGap,
	Jumbo,
	Tabs,
	tokens,
} from '@treams/treams-ui';

import Authentication from './Authentication';
import Cycles from './Cycles';
import PersonalInformation from './PersonalInformation';
import SentEmails from './SentEmails';
import SupportUserMenu from './SupportUserMenu';
import JobPositions from './job-positions/JobPositions';
import Roles from './roles/Roles';

export default function EmployeeSettings(props: { id: string }) {
	const { t } = useTranslation();
	const [tab, setTab] = useState(0);
	const isSupport = useIsSupport();
	const connector = useWorkspaceConnector();
	const multiRoleConversion = useFeature(graphql.FeatureName.MultiRoleConversion);
	const { canViewAndEditAllSettings, isHRBusinessPartner } = useViewerRoles();

	const identityProviders = graphql.useIdentityProvidersQuery();
	const { data, refetch } = graphql.useUserQuery({
		variables: { id: props.id },
		fetchPolicy: 'network-only',
	});
	const [archive] = graphql.useArchiveUserMutation();
	const [restore] = graphql.useRestoreUserMutation();

	const user = data?.user;
	const isObfuscated = user?.username.startsWith('obfuscated+');
	const connectorTitle = connector
		? t(`connectors.product.${connector.toLowerCase()}.title`)
		: undefined;
	const isArchived = user?.isArchived && user.archivedAt;
	const isSynced = !!connector && !!user?.externalReferenceID;
	const disableEditing = isSynced && !isSupport;

	return (
		<>
			<Jumbo
				title={t('settings.employees.edit.title', { name: user?.firstName })}
				button={
					<Flex>
						<Box>
							<Button
								mr={2}
								type="secondary"
								title={t('employee.settings.buttons.profile')}
								icon={<CircleUserIcon />}
								link={`/users/${props.id}/journey`}
							/>
						</Box>
						{!isHRBusinessPartner && (
							<Box>
								{user?.isArchived ? (
									<Button
										type="secondary"
										disabled={isObfuscated}
										title={t('common.restore')}
										onClick={async () => {
											await restore({ variables: { input: { id: user!.id } } });
										}}
									/>
								) : (
									<Button
										type="secondary"
										title={t('common.archive')}
										icon={<ArchiveIcon />}
										onClick={async () => {
											await archive({ variables: { input: { id: user!.id } } });
										}}
									/>
								)}
							</Box>
						)}
						{isSupport && user && (
							<Flex ml={3} justifyContent="center" alignItems="center">
								<SupportUserMenu user={user as graphql.User} refetch={refetch} />
							</Flex>
						)}
					</Flex>
				}
			/>
			<Container pt={`${tokens.gap.page}px`} pb={4}>
				<Box>
					{isArchived && !isSynced && (
						<Box mb={3}>
							<Callout
								color="blue"
								title={t('settings.employees.archived.title')}
								description={t('settings.employees.archived.description', {
									date: moment(user?.archivedAt).format('LL'),
								})}
							/>
						</Box>
					)}

					{isSynced && isArchived && (
						<Box mb={3}>
							<Callout
								color="blue"
								title={t('settings.employees.archived.title')}
								description={t('settings.employees.archived-external.description', {
									date: moment(user?.archivedAt).format('LL'),
									name: user?.firstName,
									connector: connectorTitle,
								})}
							/>
						</Box>
					)}

					{isSynced && !isArchived && (
						<Box mb={3}>
							<Callout
								color="blue"
								title={t('settings.employees.external.title', {
									connector: connectorTitle,
								})}
								description={t('settings.employees.external.description', {
									name: user?.firstName,
									connector: connectorTitle,
								})}
							/>
						</Box>
					)}

					<Flex mb={5}>
						<Box flexGrow={1} flexShrink={1} flexBasis={0} mr={4}>
							<PersonalInformation user={user as graphql.User} disableEditing={disableEditing} />
						</Box>
						<Box flexGrow={1} flexShrink={1} flexBasis={0}>
							<Authentication
								user={user as graphql.User}
								identityProviders={
									identityProviders.data?.identityProviders as graphql.IdentityProvider[]
								}
							/>
						</Box>
					</Flex>
					{canViewAndEditAllSettings && (
						<FlexGap flexDirection="column" gap={tokens.gap.section} width="100%">
							<Tabs>
								<Tabs.Item
									title={t('settings.employees.cycles.title')}
									onClick={() => setTab(0)}
									selected={tab === 0}
								/>
								<Tabs.Item
									title={t(
										multiRoleConversion
											? 'settings.employees.jobPositions.roles.title'
											: 'settings.employees.jobPositions.title',
									)}
									onClick={() => setTab(1)}
									selected={tab === 1}
								/>
								<Tabs.Item
									title={t('settings.employees.roles.title')}
									onClick={() => setTab(2)}
									selected={tab === 2}
								/>
								<Tabs.Item
									title={t('settings.employees.sentEmails.title')}
									onClick={() => setTab(3)}
									selected={tab === 3}
								/>
							</Tabs>
							{tab === 0 && <Cycles id={props.id} />}
							{tab === 1 && <JobPositions id={props.id} />}
							{tab === 2 && <Roles id={props.id} />}
							{tab === 3 && <SentEmails id={props.id} />}
						</FlexGap>
					)}
				</Box>
			</Container>
		</>
	);
}
